import { useState, useEffect } from 'react';
import { BarChartComponent, LineChartComponent, PieChartComponent } from '../Charts/ChartComponents';
import DataTable from '../Common/DataTable';
import { attendanceAPI } from '../../services/api';

// ─── helpers ─────────────────────────────────────────────────────────────────

function attColorClass(pct) {
  if (pct >= 95) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (pct >= 90) return 'text-amber-700 bg-amber-50 border-amber-200';
  if (pct >= 85) return 'text-orange-700 bg-orange-50 border-orange-200';
  return 'text-red-700 bg-red-50 border-red-200';
}

function PctCell(v) {
  if (v == null) return <span className="text-gray-400">—</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${attColorClass(v)}`}>
      {v}%
    </span>
  );
}

function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-gray-200 rounded ${className}`} />;
}

function ChartSkeleton() {
  return (
    <div className="glass-card p-4 space-y-3">
      <Skeleton className="h-4 w-36" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="glass-card p-4 space-y-2">
      <Skeleton className="h-4 w-40 mb-3" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

// ─── column definitions ───────────────────────────────────────────────────────

const performerColumns = [
  { key: 'name', label: 'Employee', sortable: true },
  { key: 'dept_name', label: 'Department', sortable: true },
  { key: 'pct', label: 'Attendance %', sortable: true, render: PctCell },
  { key: 'late_count', label: 'Late Count', sortable: true },
];

const defaulterColumns = [
  { key: 'name', label: 'Employee', sortable: true },
  { key: 'dept_name', label: 'Department', sortable: true },
  { key: 'pct', label: 'Attendance %', sortable: true, render: PctCell },
  { key: 'absent_days', label: 'Absent Days', sortable: true },
  { key: 'late_count', label: 'Late Count', sortable: true },
];

const habitualLateColumns = [
  { key: 'name', label: 'Employee', sortable: true },
  { key: 'dept_name', label: 'Department', sortable: true },
  {
    key: 'late_count',
    label: 'Late Count',
    sortable: true,
    render: v => (
      <span className="text-amber-700 font-bold">{v}</span>
    ),
  },
  { key: 'absent_days', label: 'Absent Days', sortable: true },
  { key: 'pct', label: 'Attendance %', sortable: true, render: PctCell },
];

// ─── main component ───────────────────────────────────────────────────────────

export default function AttendanceAnalytics({ yearId }) {
  const [lateThreshold, setLateThreshold] = useState(3);
  const [pendingThreshold, setPendingThreshold] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!yearId) return;

    async function fetchData() {
      setLoading(true);
      try {
        const res = await attendanceAPI.getAnalytics({
          academic_year_id: yearId,
          late_threshold: lateThreshold,
        });
        setData(res.data);
      } catch (err) {
        console.error('AttendanceAnalytics fetch error', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [yearId, lateThreshold]);

  // Build monthly trend data — map month_name to 3-char abbreviation
  const monthlyTrend = (data?.monthly_trend || []).map(row => ({
    ...row,
    month_abbr: row.month_name ? row.month_name.substring(0, 3) : '',
  }));

  // Build distribution data for pie chart
  const dist = data?.distribution || {};
  const pieData = [
    { name: 'Present', value: dist.present },
    { name: 'Absent', value: dist.absent },
    { name: 'On Leave', value: dist.on_leave },
    { name: 'Late', value: dist.late },
    { name: 'Half Day', value: dist.half_day },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6">

      {/* ── Header row ───────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-gray-800">Analytics</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500 whitespace-nowrap">
            Habitual Late &gt;
          </label>
          <input
            type="number"
            min={1}
            max={20}
            value={pendingThreshold}
            onChange={e => setPendingThreshold(Number(e.target.value))}
            onBlur={() => setLateThreshold(pendingThreshold)}
            onKeyDown={e => e.key === 'Enter' && setLateThreshold(pendingThreshold)}
            className="w-14 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-sky-400"
          />
          <span className="text-sm text-gray-500">times</span>
        </div>
      </div>

      {/* ── Row 1: Three trend line charts ───────────────────────────────── */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <LineChartComponent
            title="Monthly Attendance %"
            data={monthlyTrend}
            xKey="month_abbr"
            lines={[{ key: 'att_pct', label: 'Attendance %', color: '#0ea5e9' }]}
            height={200}
          />
          <LineChartComponent
            title="Monthly Late Arrivals"
            data={monthlyTrend}
            xKey="month_abbr"
            lines={[{ key: 'late_records', label: 'Late Arrivals', color: '#f59e0b' }]}
            height={200}
          />
          <LineChartComponent
            title="Monthly Leave Days"
            data={monthlyTrend}
            xKey="month_abbr"
            lines={[{ key: 'leave_records', label: 'Leave Days', color: '#8b5cf6' }]}
            height={200}
          />
        </div>
      )}

      {/* ── Row 2: Bar chart + Pie chart ─────────────────────────────────── */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BarChartComponent
            title="Department-wise Attendance"
            data={data?.department_bar || []}
            xKey="dept"
            bars={[{ key: 'pct', label: 'Attendance %', color: '#10b981' }]}
            height={260}
          />
          <PieChartComponent
            title="Attendance Distribution"
            data={pieData}
            nameKey="name"
            valueKey="value"
            donut
            height={260}
          />
        </div>
      )}

      {/* ── Row 3: Top performers + Top defaulters ───────────────────────── */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TableSkeleton />
          <TableSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="glass-card p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Top 10 Attendance Performers
            </h3>
            <DataTable
              columns={performerColumns}
              data={data?.top_performers || []}
              loading={false}
              emptyText="No data available"
            />
          </div>
          <div className="glass-card p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Top 10 Attendance Defaulters
            </h3>
            <DataTable
              columns={defaulterColumns}
              data={data?.top_defaulters || []}
              loading={false}
              emptyText="No data available"
            />
          </div>
        </div>
      )}

      {/* ── Row 4: Habitual late comers ──────────────────────────────────── */}
      {loading ? (
        <TableSkeleton />
      ) : (
        <div className="glass-card p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Habitual Late Comers (More than {lateThreshold} late arrival{lateThreshold !== 1 ? 's' : ''})
          </h3>
          <DataTable
            columns={habitualLateColumns}
            data={data?.habitual_late || []}
            loading={false}
            emptyText="No habitual late comers — great punctuality!"
          />
        </div>
      )}
    </div>
  );
}
